// src/pages/Accounting/BankTransactions/components/BankConnectModal.tsx
//
// NOTE: Mock UX only. In production this entire modal is replaced with
// a Plaid Link iframe or Flinks Connect widget, which handles credentials
// on their side. Novala never sees the user's bank password.

import React, { useState, useEffect } from "react";
import { X, Lock } from "lucide-react";
import { PALETTE } from "../constants";
import type { Bank } from "../types";
import { BankLogo } from "./BankLogo";

type Stage = "credentials" | "connecting" | "pulling" | "done";

interface BankConnectModalProps {
  bank: Bank;
  onClose: () => void;
  onConnected: (bank: Bank) => void;
}

export function BankConnectModal({ bank, onClose, onConnected }: BankConnectModalProps) {
  const [stage, setStage] = useState<Stage>("credentials");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) return;
    setStage("connecting");
  };

  useEffect(() => {
    if (stage === "connecting") {
      const t = setTimeout(() => setStage("pulling"), 1600);
      return () => clearTimeout(t);
    }
    if (stage === "pulling") {
      const t = setTimeout(() => setStage("done"), 1400);
      return () => clearTimeout(t);
    }
    if (stage === "done") {
      const t = setTimeout(() => onConnected(bank), 900);
      return () => clearTimeout(t);
    }
  }, [stage, bank, onConnected]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 26, 26, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 14,
          width: "100%",
          maxWidth: 440,
          padding: "28px 32px",
          boxShadow: "0 20px 50px rgba(14, 26, 26, 0.2)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <BankLogo code={bank.code} shortName={bank.shortName} size="md" />
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: PALETTE.ink4,
                  marginBottom: 2,
                }}
              >
                Connect bank
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: PALETTE.ink }}>
                {bank.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: PALETTE.ink3,
              padding: 4,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {stage === "credentials" && (
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                background: PALETTE.panel,
                borderRadius: 8,
                padding: "10px 12px",
                marginBottom: 20,
                fontSize: 12,
                color: PALETTE.ink3,
                fontWeight: 500,
              }}
            >
              <Lock size={14} />
              <span>
                We use bank-level encryption via Plaid/Flinks. Novala never stores your login.
              </span>
            </div>

            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: PALETTE.ink2,
                marginBottom: 6,
              }}
            >
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              style={{
                width: "100%",
                height: 42,
                border: `1px solid ${PALETTE.line}`,
                borderRadius: 8,
                padding: "0 14px",
                fontSize: 14,
                color: PALETTE.ink,
                outline: "none",
                background: "#fff",
                marginBottom: 14,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />

            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: PALETTE.ink2,
                marginBottom: 6,
              }}
            >
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: "100%",
                height: 42,
                border: `1px solid ${PALETTE.line}`,
                borderRadius: 8,
                padding: "0 14px",
                fontSize: 14,
                color: PALETTE.ink,
                outline: "none",
                background: "#fff",
                marginBottom: 20,
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={onClose}
                style={{
                  height: 40,
                  padding: "0 18px",
                  borderRadius: 9,
                  fontSize: 13,
                  fontWeight: 700,
                  border: `1px solid ${PALETTE.line}`,
                  background: "#fff",
                  color: PALETTE.ink,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!username || !password}
                style={{
                  height: 40,
                  padding: "0 20px",
                  borderRadius: 9,
                  fontSize: 13,
                  fontWeight: 700,
                  border: "none",
                  background: PALETTE.ink,
                  color: "#fff",
                  cursor: !username || !password ? "not-allowed" : "pointer",
                  opacity: !username || !password ? 0.5 : 1,
                  fontFamily: "inherit",
                }}
              >
                Securely sign in
              </button>
            </div>
          </form>
        )}

        {(stage === "connecting" || stage === "pulling" || stage === "done") && (
          <div style={{ padding: "32px 0", textAlign: "center" }}>
            {stage === "done" ? (
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: PALETTE.tealDark,
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 28,
                  fontWeight: 800,
                  marginBottom: 20,
                }}
              >
                ✓
              </div>
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  border: `3px solid ${PALETTE.lineSoft}`,
                  borderTopColor: PALETTE.vela,
                  borderRadius: "50%",
                  animation: "bt-spin 1s linear infinite",
                  margin: "0 auto 20px",
                }}
              />
            )}
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                color: PALETTE.ink,
                marginBottom: 6,
              }}
            >
              {stage === "connecting" && `Connecting to ${bank.name}...`}
              {stage === "pulling" && "Pulling your transactions..."}
              {stage === "done" && "Connected!"}
            </div>
            <div style={{ fontSize: 12.5, color: PALETTE.ink3, fontWeight: 500 }}>
              {stage === "connecting" && "This usually takes a few seconds."}
              {stage === "pulling" && "Vela is cleaning and categorizing as they arrive."}
              {stage === "done" && "Taking you to your bank transactions..."}
            </div>
            <style>{`@keyframes bt-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>
    </div>
  );
}